import React, { JSX, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import classNames from 'classnames'
import {
  Squares2X2Icon,
  CloudIcon,
  ChartBarIcon,
  TrophyIcon,
  ArrowsRightLeftIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
  ArrowLeftIcon,
  ChevronUpDownIcon,
  CheckIcon,
  Bars3Icon,
  RectangleGroupIcon,
  DevicePhoneMobileIcon,
  ChatBubbleLeftRightIcon,
  FaceSmileIcon,
  LockClosedIcon,
  VideoCameraIcon,
  GiftIcon,
  MegaphoneIcon
} from '@heroicons/react/24/outline'
import { ADMIN_STREAMS_PATH, EVENTS_PATH, eventSectionPath, EventSection } from 'lib/paths'
import { EventListItem } from 'components/EventsSidebar'
import { useEntitlements } from 'context/plan'
import { useTickerBarSurfaceVisible } from 'hooks/useTickerBarSurfaceVisible'
import { PlanFeatureKey } from 'types/api'

type SectionGroupId =
  | 'event-details'
  | 'interactive-content'
  | 'audience-feeds'
  | 'camera-control'
  | 'live-control'
  | 'users'

type LockBehavior = 'lock' | 'hide'

interface SectionLink {
  key: EventSection
  name: string
  icon: React.ForwardRefExoticComponent<Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
    title?: string
    titleId?: string
  } & React.RefAttributes<SVGSVGElement>>
  requires?: PlanFeatureKey
  lockBehavior?: LockBehavior
  staffOnly?: boolean
  tickerBarOnly?: boolean
}

interface SectionGroup {
  id: SectionGroupId
  heading: string
  requires?: PlanFeatureKey
  lockBehavior?: LockBehavior
  links: SectionLink[]
}

const SECTION_GROUPS: SectionGroup[] = [
  {
    id: 'event-details',
    heading: 'Stream Details',
    links: [
      { key: 'overview', name: 'Overview', icon: Squares2X2Icon },
      { key: 'integrations', name: 'Integrations', icon: CloudIcon, requires: 'interactiveContent', lockBehavior: 'lock' }
    ]
  },
  {
    id: 'audience-feeds',
    heading: 'Audience Feeds',
    links: [
      { key: 'chats', name: 'Chat', icon: ChatBubbleLeftRightIcon },
      { key: 'emote-walls', name: 'Emote Walls', icon: FaceSmileIcon },
      { key: 'message-tickers', name: 'Message tickers', icon: MegaphoneIcon, tickerBarOnly: true }
    ]
  },
  {
    id: 'interactive-content',
    heading: 'Interactive Content',
    requires: 'interactiveContent',
    lockBehavior: 'lock',
    links: [
      { key: 'scoreboard', name: 'Scoreboard', icon: TrophyIcon },
      { key: 'polls', name: 'Polls', icon: ChartBarIcon },
      { key: 'trivia', name: 'Trivia', icon: QuestionMarkCircleIcon },
      { key: 'tug-of-wars', name: 'Tug of Wars', icon: ArrowsRightLeftIcon },
      { key: 'giveaways', name: 'Giveaways', icon: GiftIcon, staffOnly: true }
    ]
  },
  {
    id: 'camera-control',
    heading: 'Camera Control',
    links: [
      { key: 'camera-controls', name: 'Camera Controls', icon: VideoCameraIcon }
    ]
  },
  {
    id: 'live-control',
    heading: 'Live Control',
    requires: 'interactiveContent',
    lockBehavior: 'hide',
    links: [
      { key: 'desktop-overlay', name: 'Desktop Overlay', icon: RectangleGroupIcon },
      { key: 'mobile-overlay', name: 'Mobile Overlay', icon: DevicePhoneMobileIcon }
    ]
  },
  {
    id: 'users',
    heading: 'Users',
    links: [
      { key: 'access', name: 'Access', icon: UserGroupIcon }
    ]
  }
]

const STATUS_DOT: Record<EventListItem['status'], string> = {
  ACTIVE: 'bg-emerald-500',
  INACTIVE: 'bg-amber-500',
  ENDED: 'bg-zinc-500'
}

const CurrentEventPicker = ({
  current,
  options,
  onSelect,
  showsLifecycleChrome,
  listFailed,
  onRetryList
}: {
  current: EventListItem
  options: EventListItem[]
  onSelect: (eventId: string) => void
  showsLifecycleChrome: boolean
  listFailed: boolean
  onRetryList?: () => void
}): JSX.Element => {
  const [open, setOpen] = useState(false)
  return (
    <div className='relative'>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        data-testid='current-event-picker'
        className='flex w-full items-center justify-between gap-2 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary hover:bg-dm-bg-accent/60'
      >
        <span className='flex items-center gap-2 truncate'>
          {showsLifecycleChrome && (
            <span className={classNames('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[current.status])} />
          )}
          <span className='truncate'>Current Stream</span>
        </span>
        <ChevronUpDownIcon className='size-4 shrink-0 text-dm-text-accent' aria-hidden='true' />
      </button>
      {open && (
        <ul className='absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded-md border border-white/10 bg-dm-bg-secondary py-1 shadow-lg'>
          {options.length === 0 && (
            listFailed
              ? (
                <li className='flex items-center justify-between gap-2 px-3 py-2 text-xs text-dm-text-accent'>
                  <span>Couldn't load your streams.</span>
                  <button
                    type='button'
                    onClick={() => { setOpen(false); onRetryList?.() }}
                    data-testid='current-event-picker-retry'
                    className='shrink-0 rounded-md border border-white/10 px-2 py-0.5 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                  >
                    Try again
                  </button>
                </li>
                )
              : (
                <li data-testid='current-event-picker-empty' className='px-3 py-2 text-xs text-dm-text-accent'>
                  No other streams yet.
                </li>
                )
          )}
          {options.map((opt) => {
            const isCurrent = opt.eventId === current.eventId
            return (
              <li key={opt.eventId}>
                <button
                  type='button'
                  onClick={() => { setOpen(false); onSelect(opt.eventId) }}
                  className={classNames(
                    'flex w-full items-start justify-between gap-2 px-3 py-2 text-left text-sm',
                    isCurrent
                      ? 'bg-brand/10 text-dm-text-primary'
                      : 'text-dm-text-secondary hover:bg-white/5 hover:text-dm-text-primary'
                  )}
                >
                  <span className='flex min-w-0 flex-1 items-start gap-2'>
                    {showsLifecycleChrome && (
                      <span className={classNames('mt-1.5 h-2 w-2 shrink-0 rounded-full', STATUS_DOT[opt.status])} />
                    )}
                    <span className='min-w-0 flex-1 break-words'>{opt.name}</span>
                  </span>
                  {isCurrent && <CheckIcon className='mt-1 size-4 shrink-0 text-brand' aria-hidden='true' />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export const InEventSidebar = ({
  event,
  allEvents,
  onClose,
  staffView = false,
  listFailed = false,
  onRetryList
}: {
  event: EventListItem
  allEvents: EventListItem[]
  onClose: () => void
  staffView?: boolean
  listFailed?: boolean
  onRetryList?: () => void
}): JSX.Element => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { status, featureFor, staffUncapped } = useEntitlements()
  const tickerBarsVisible = useTickerBarSurfaceVisible() === 'visible'

  const isEntitled = (requires: PlanFeatureKey | undefined): boolean =>
    requires === undefined || status !== 'ready' || featureFor(requires)

  const showsLifecycleChrome = status !== 'ready' || featureFor('interactiveContent')

  const groupHidden = (g: SectionGroup): boolean => {
    if (g.id === 'live-control' && event.status === 'ENDED') return true
    if (g.lockBehavior === 'hide' && !isEntitled(g.requires)) return true
    if (g.id === 'camera-control' && !staffUncapped) return true
    return false
  }

  const visibleGroups = SECTION_GROUPS.filter((g) => !groupHidden(g))

  const handleSelect = (eventId: string): void => {
    void navigate(eventSectionPath(eventId, 'overview'))
  }

  return (
    <aside
      data-testid='in-event-sidebar'
      className='flex h-screen w-[14.58rem] flex-col border-r border-white/5 bg-dm-bg-secondary sticky top-0'
    >
      <div className='border-b border-white/5 px-5 pt-5 pb-4'>
        <div className='mb-3 flex items-start justify-between gap-3'>
          {staffView
            ? (
              <div className='min-w-0 flex-1'>
                <h2 className='text-sm font-semibold leading-snug text-dm-text-primary'>
                  {event.name}
                </h2>

                <span
                  data-testid='in-event-staff-view'
                  title='Superuser only. You are not a member of this stream.'
                  className='mt-1.5 inline-flex items-center rounded-md border border-dashed border-brand/45 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-brand'
                >
                  Staff view
                </span>
              </div>
              )
            : (
              <h2 className='flex-1 text-sm font-semibold leading-snug text-dm-text-primary'>
                {event.name}
              </h2>
              )}
          <button
            type='button'
            onClick={onClose}
            data-testid='in-event-sidebar-close'
            title='Hide sidebar'
            className='shrink-0 rounded-md p-1 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
          >
            <Bars3Icon className='size-5' aria-hidden='true' />
            <span className='sr-only'>Hide sidebar</span>
          </button>
        </div>
        <CurrentEventPicker
          current={event}
          options={allEvents}
          onSelect={handleSelect}
          showsLifecycleChrome={showsLifecycleChrome}
          listFailed={listFailed}
          onRetryList={onRetryList}
        />
        <Link
          to={staffView ? ADMIN_STREAMS_PATH : EVENTS_PATH}
          data-testid='in-event-back'
          className='mt-3 flex items-center gap-1.5 text-xs text-brand hover:text-brand/80'
        >
          <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
          {staffView ? 'Back to All streams' : 'Back to Streams'}
        </Link>
      </div>

      <nav className='flex-1 overflow-y-auto px-2 py-3'>
        {visibleGroups.map((group) => (
          <div key={group.id} className='mb-4'>
            <h3 className='px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
              {group.heading}
            </h3>
            <ul className='space-y-0.5'>
              {group.links.map((link) => {
                const { key, name, icon: Icon } = link
                if (link.staffOnly === true && !staffUncapped) return null
                if (link.tickerBarOnly === true && !tickerBarsVisible) return null
                const requires = link.requires ?? group.requires
                const lockBehavior = link.lockBehavior ?? group.lockBehavior
                const gatedOff = requires !== undefined && !isEntitled(requires)
                if (gatedOff && lockBehavior === 'hide') return null
                const locked = gatedOff && lockBehavior === 'lock'
                const href = eventSectionPath(event.eventId, key)
                const active = pathname === href || pathname.startsWith(`${href}/`)
                return (
                  <li key={key}>
                    <Link
                      to={href}
                      data-testid={`in-event-section-${key}`}
                      className={classNames(
                        'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                        active
                          ? 'bg-brand/10 text-dm-text-primary ring-1 ring-brand/30'
                          : 'text-dm-text-secondary hover:bg-white/5 hover:text-dm-text-primary'
                      )}
                    >
                      <Icon className='size-4 shrink-0' aria-hidden='true' />
                      <span>{name}</span>
                      {locked && (
                        <LockClosedIcon
                          data-testid={`in-event-section-${key}-lock`}
                          className='ml-auto size-3.5 shrink-0 text-dm-text-accent'
                          aria-hidden='true'
                        />
                      )}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  )
}
